import React, { createContext, useContext, useState, ReactNode, useMemo, useCallback, useEffect } from 'react'
import { useRooms } from '../../hooks/coreApi/useRooms'

// G004-1: 予約作成（日付選択）, G004-2: 予約作成（ルーム・時刻選択）, G004-3: 予約作成（タイトル入力）, G004-4: 予約作成（パスワード入力）, G004-5: 予約作成（確認）
export type ReservationStep = 
  | 'room-date'  // G004-1: 予約作成（日付選択）
  | 'room-time'  // G004-2: 予約作成（ルーム・時刻選択）
  | 'title'      // G004-3: 予約作成（タイトル入力）
  | 'password'   // G004-4: 予約作成（パスワード入力）
  | 'confirm'    // G004-5: 予約作成（確認）

export type ReservationState = {
  roomId: number | null
  date: string | null
  startTime: string | null
  endTime: string | null
  title: string
  password: string
}

type ReservationContextType = {
  state: ReservationState
  updateState: (newState: Partial<ReservationState>) => void
  step: ReservationStep
  goToNext: (newState?: Partial<ReservationState>) => void
  goBack: () => void
}

const ReservationContext = createContext<ReservationContextType | undefined>(undefined)

// ステップの順序を配列として定義
const STEP_SEQUENCE: ReservationStep[] = ['room-date', 'room-time', 'title', 'password', 'confirm']

// ステップのインデックスを取得
const getStepIndex = (step: ReservationStep): number => STEP_SEQUENCE.indexOf(step)

// 次のステップを取得
const getNextStep = (currentStep: ReservationStep): ReservationStep => {
  const currentIndex = getStepIndex(currentStep)
  const nextIndex = Math.min(currentIndex + 1, STEP_SEQUENCE.length - 1)
  return STEP_SEQUENCE[nextIndex]
}

// 前のステップを取得
const getPrevStep = (currentStep: ReservationStep): ReservationStep => {
  const currentIndex = getStepIndex(currentStep)
  const prevIndex = Math.max(currentIndex - 1, 0)
  return STEP_SEQUENCE[prevIndex]
}

export const ReservationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [step, setStep] = useState<ReservationStep>('room-date')
  const [state, setState] = useState<ReservationState>({
    roomId: null,
    date: null,
    startTime: null,
    endTime: null,
    title: '',
    password: '',
  })

  // 部屋の一覧を取得
  const roomsQuery = useRooms({ isPasswordProtected: true })

  // 部屋の一覧が取得できたら、最初の部屋を自動選択
  useEffect(() => {
    const rooms = roomsQuery.data?.rooms ?? []
    if (rooms.length > 0 && state.roomId === null) {
      setState(prev => ({
        ...prev,
        roomId: rooms[0].id
      }))
    }
  }, [roomsQuery.data, state.roomId])

  // ブラウザバックのハンドリング
  useEffect(() => {
    // ステップが変わるごとにstate履歴に追加
    if (step !== 'room-date') {
      window.history.pushState({ step }, '')
    }

    // ブラウザバックイベントのリスナー
    const handlePopState = (event: PopStateEvent) => {
      const prevStep = event.state?.step
      if (prevStep) {
        setStep(prevStep)
      } else {
        // 履歴に保存されたステップがない場合は前のステップに戻る
        setStep(getPrevStep(step))
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [step])

  const updateState = useCallback((newState: Partial<ReservationState>) => {
    setState((prev) => ({ ...prev, ...newState }))
  }, [])

  const goToNext = useCallback((newState?: Partial<ReservationState>) => {
    if (newState) {
      updateState(newState)
    }

    setStep(currentStep => getNextStep(currentStep))
  }, [updateState])

  const goBack = useCallback(() => {
    setStep(currentStep => {
      // ブラウザの履歴APIを使って戻る
      window.history.back()
      return currentStep // 実際の更新はpopstateイベントで行われる
    })
  }, [])

  const contextValue = useMemo(() => ({
    state,
    updateState,
    step,
    goToNext,
    goBack
  }), [state, step, updateState, goToNext, goBack]);

  return (
    <ReservationContext.Provider value={contextValue}>
      {children}
    </ReservationContext.Provider>
  )
}

export const useReservation = (): ReservationContextType => {
  const context = useContext(ReservationContext)
  if (context === undefined) {
    throw new Error('useReservation must be used within a ReservationProvider')
  }
  return context
} 